import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { BadgeCheck, CalendarCheck, Video, XCircle, ClipboardCheck, IdCard, Handshake, RefreshCw } from "lucide-react";
import api, { formatApiError } from "../api";
import { useAuth } from "../context/AuthContext";

const OFFICER = {
  name: "Sarah Thornton",
  title: "Senior Verification Officer",
  team: "Veritas Trust & Safety Team",
  url: "https://images.unsplash.com/photo-1706565029671-50cfa8bdf818?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjA0MHMlMjBtYW4lMjB3b21hbiUyMHBvcnRyYWl0fGVufDB8fHx8MTc4OTYzNTEzNHww&ixlib=rb-4.1.0&q=85",
};

function formatSlot(iso) {
  const d = new Date(iso);
  return {
    day: d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }),
    time: d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }),
  };
}

export default function Verification() {
  const { user, refreshUser } = useAuth();
  const [status, setStatus] = useState(null);
  const [slots, setSlots] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [inCall, setInCall] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/verification/status");
      setStatus(data);
      if (data.status !== "verified") {
        const s = await api.get("/verification/slots");
        setSlots(s.data.slots.filter((x) => x.available));
      }
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const book = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.post("/verification/book", { slot: selected });
      toast.success("Interview booked. We'll see you on camera!");
      await refreshUser();
      await load();
      setSelected(null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!status) return <div className="py-24 text-center text-stone-500">Loading…</div>;

  const booking = status.booking;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
      <p className="eyebrow mb-2">Trust &amp; safety</p>
      <h1 className="font-display text-3xl sm:text-4xl text-stone-900 mb-2">Your video verification</h1>
      <p className="text-stone-600 text-sm mb-10 max-w-2xl">
        A friendly 5-minute video call with a Veritas verification officer. Once approved, you can browse
        members, like, match, and message.
      </p>

      {/* Status timeline */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10" data-testid="verification-status-tracker">
        {[
          { key: "unverified", label: "Pending slot" },
          { key: "scheduled", label: "Interview scheduled" },
          { key: "in_review", label: "In verification" },
          { key: "verified", label: "Approved" },
        ].map((s) => {
          const active =
            (s.key === "unverified" && ["unverified", "rejected"].includes(status.status)) ||
            (s.key === "scheduled" && status.status === "scheduled") ||
            (s.key === "verified" && status.status === "verified");
          return (
            <div
              key={s.key}
              className={`rounded-xl border px-4 py-3 text-sm font-medium ${
                active ? "bg-[#1F3A34] text-[#FAF8F5] border-[#1F3A34]" : "bg-white text-stone-400 border-[#E7DFD5]"
              }`}
            >
              {s.label}
            </div>
          );
        })}
      </div>

      {/* Verified state */}
      {status.status === "verified" && (
        <div className="card-surface p-8 text-center" data-testid="verification-approved-card">
          <span className="mx-auto w-14 h-14 rounded-full bg-[#ECFDF5] border border-[#A7F3D0] flex items-center justify-center text-[#065F46]">
            <BadgeCheck size={26} />
          </span>
          <h2 className="font-display text-2xl text-stone-900 mt-4">You're officially video-verified</h2>
          <p className="text-sm text-stone-500 mt-2 font-dm uppercase tracking-wider">
            {status.verified_at ? `Verified on ${new Date(status.verified_at).toLocaleDateString()}` : "Verified member"}
          </p>
          <p className="text-sm text-stone-600 mt-4 max-w-md mx-auto">
            Your profile now carries the Veritas Verified seal. Browse members and start genuine conversations.
          </p>
        </div>
      )}

      {/* Rejected state */}
      {status.status === "rejected" && (
        <div className="card-surface p-6 mb-8 border-l-4 !border-l-red-300" data-testid="verification-rejected-card">
          <div className="flex items-start gap-3">
            <XCircle size={20} className="text-red-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-stone-800">Your interview needs a re-take</h3>
              <p className="text-sm text-stone-600 mt-1">
                {booking?.notes
                  ? `Officer note: ${booking.notes}`
                  : "We couldn't complete verification last time. Please book a new slot below."}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Scheduled state: meeting room */}
      {status.status === "scheduled" && booking && (
        <div className="card-surface overflow-hidden mb-8" data-testid="verification-scheduled-card">
          <div className="p-6 flex flex-col sm:flex-row sm:items-center gap-5 border-b border-[#E7DFD5]">
            <img src={OFFICER.url} alt={OFFICER.name} className="w-16 h-16 rounded-2xl object-cover" />
            <div className="flex-1">
              <p className="font-semibold text-stone-800">{OFFICER.name}</p>
              <p className="text-xs text-stone-500">
                {OFFICER.title} · {OFFICER.team}
              </p>
              <p className="text-sm text-stone-600 mt-2 flex items-center gap-2">
                <CalendarCheck size={14} className="text-[#C58342]" />
                {formatSlot(booking.slot).day} at {formatSlot(booking.slot).time}
              </p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setInCall((v) => !v)} className="btn-primary text-sm" data-testid="verification-join-call-btn">
                <Video size={15} /> {inCall ? "Leave call" : "Join video call"}
              </button>
              <button
                onClick={async () => {
                  await load();
                  toast("Slots refreshed.");
                }}
                className="btn-secondary text-sm"
                data-testid="verification-reschedule-btn"
              >
                <RefreshCw size={14} /> Reschedule
              </button>
            </div>
          </div>

          {inCall && (
            <div className="bg-stone-950" data-testid="video-call-room">
              <iframe
                src={booking.meeting_url}
                title="Veritas40+ verification interview"
                className="w-full h-[480px] border-0"
                allow="camera; microphone; fullscreen; display-capture; autoplay"
              />
              <div className="px-6 py-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-stone-300">
                <span className="font-dm uppercase tracking-widest text-[#C58342]">During the call</span>
                <span className="flex items-center gap-1.5"><IdCard size={13} /> Have a photo ID ready</span>
                <span className="flex items-center gap-1.5"><ClipboardCheck size={13} /> Live face match with your profile photo</span>
                <span className="flex items-center gap-1.5"><Handshake size={13} /> Sign the Safe Member Pledge</span>
              </div>
            </div>
          )}

          {!inCall && (
            <div className="p-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { icon: <IdCard size={16} />, title: "Photo ID", text: "Have any government-issued photo ID within reach." },
                { icon: <ClipboardCheck size={16} />, title: "Face match", text: "We'll match you live on camera with your profile photo." },
                { icon: <Handshake size={16} />, title: "Safe pledge", text: "A one-line promise to treat members with honesty." },
              ].map((c) => (
                <div key={c.title} className="rounded-xl bg-[#F3EFEA] p-4">
                  <span className="text-[#1F3A34]">{c.icon}</span>
                  <p className="font-medium text-sm text-stone-800 mt-2">{c.title}</p>
                  <p className="text-xs text-stone-500 mt-1">{c.text}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Booking state */}
      {["unverified", "rejected"].includes(status.status) && (
        <div className="card-surface p-6" data-testid="verification-booking-panel">
          <h2 className="font-display text-xl text-stone-900 mb-1">Pick your interview slot</h2>
          <p className="text-sm text-stone-500 mb-6">15 minutes with a Veritas verification officer. All times in your local timezone.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-96 overflow-y-auto pr-1">
            {slots.map((s) => {
              const f = formatSlot(s.slot);
              const isSel = selected === s.slot;
              return (
                <button
                  key={s.slot}
                  onClick={() => setSelected(s.slot)}
                  className={`rounded-xl border px-3 py-3 text-left transition-colors duration-150 ${
                    isSel ? "bg-[#1F3A34] text-[#FAF8F5] border-[#1F3A34]" : "bg-white border-[#E7DFD5] hover:border-[#C58342]"
                  }`}
                  data-testid="verification-slot-select"
                >
                  <p className={`text-xs font-dm uppercase tracking-wider ${isSel ? "text-[#C58342]" : "text-stone-400"}`}>{f.day}</p>
                  <p className="text-sm font-semibold mt-0.5">{f.time}</p>
                </button>
              );
            })}
          </div>
          <button onClick={book} disabled={!selected || busy} className="btn-accent mt-6 disabled:opacity-50" data-testid="verification-confirm-booking-btn">
            <CalendarCheck size={16} /> {busy ? "Booking…" : "Confirm booking"}
          </button>
        </div>
      )}
    </div>
  );
}
